import React, { useMemo, useState } from "react"
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Card,
  CardContent,
  alpha,
  useTheme
} from "@mui/material"
import { Inventory, CheckCircle, Schedule, Search } from "@mui/icons-material"
import type { AuditProduct } from "../types"
import { AuditProductStatus } from "src/graphql/quark/quark.type"
import { Stack } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"
import MinimulTabs from "src/shared/components/MinimalTabs/MinimulTabs"

interface ProductSelectionProps {
  label: string
  description: string
  products: AuditProduct[]
  onSelectProduct: (product: AuditProduct) => void
}

const ProductSelection: React.FC<ProductSelectionProps> = ({ label, description, products, onSelectProduct }) => {
  const theme = useTheme()
  const [filter, setFilter] = useState<string>("PENDING")
  const [search, setSearch] = useState<string>("")

  const filteredProducts = useMemo(() => {
    // Filter by status (filter) and product name (search), optimized for performance
    const normalizedSearch = search.trim().toLowerCase()
    const filterStatus = filter === "all" ? null : filter

    return products.filter((product) => {
      // Filter by status if not "all"
      const statusMatch = !filterStatus || filterStatus === "all" || product.status === filterStatus
      // Filter by product name search
      const nameMatch = !normalizedSearch || product.name?.toLowerCase().includes(normalizedSearch)
      return statusMatch && nameMatch
    })
  }, [products, search, filter])

  const getStatusIcon = (status: AuditProduct["status"]) => {
    switch (status) {
      case AuditProductStatus.Completed:
        return <CheckCircle color="success" />
      case AuditProductStatus.InProgress:
        return <Schedule color="warning" />
      default:
        return <Inventory color="primary" />
    }
  }

  const getStatusColor = (status: AuditProduct["status"]) => {
    switch (status) {
      case AuditProductStatus.Completed:
        return "success"
      case AuditProductStatus.InProgress:
        return "warning"
      default:
        return "default"
    }
  }
  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Pending", value: "PENDING" },
    { label: "In Progress", value: "IN_PROGRESS" },
    { label: "Completed", value: "COMPLETED" }
  ]
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" gutterBottom color="primary.main">
            {label}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={2}>
          <OutlinedInput
            placeholder="Search by Product Name"
            sx={{ width: 300 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startAdornment={<Search sx={{ color: "text.secondary", fontSize: "1rem" }} />}
          />
          <MinimulTabs
            tabs={filterOptions}
            value={filter}
            sx={{ width: 300 }}
            handleTabChange={(value: string) => {
              setFilter(value)
            }}
          />
        </Stack>
      </Stack>
      <List sx={{ maxHeight: 500, overflow: "auto" }}>
        {filteredProducts.map((product) => (
          <ListItem key={product.id} disablePadding sx={{ mb: 1 }}>
            <Card
              sx={{
                width: "100%",
                cursor: product.status === AuditProductStatus.Completed ? "not-allowed" : "pointer",
                opacity: product.status === AuditProductStatus.Completed ? 0.6 : 1,
                "&:hover":
                  product.status !== AuditProductStatus.Completed
                    ? {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        transform: "translateY(-2px)",
                        boxShadow: theme.shadows[4]
                      }
                    : {},
                transition: "all 0.2s ease-in-out"
              }}
            >
              <CardContent sx={{ py: 1 }}>
                <ListItemButton
                  onClick={() => onSelectProduct(product)}
                  disabled={product.status === AuditProductStatus.Completed}
                  sx={{
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "transparent"
                    }
                  }}
                >
                  <ListItemIcon>{getStatusIcon(product.status)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {product.name}
                        </Typography>
                        <Chip
                          label={product.status.toLowerCase()?.replace("_", " ")}
                          size="small"
                          color={getStatusColor(product.status) as any}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        Lot Count: {product.lotCount}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </CardContent>
            </Card>
          </ListItem>
        ))}
      </List>

      {products.length === 0 && (
        <Box
          textAlign="center"
          py={6}
          sx={{
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.action.hover, 0.3)
          }}
        >
          <Inventory sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No Products Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            There are no products assigned to this audit
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default ProductSelection
